import { findBandForValue } from "./bands";
import { RainfallBand, RgbaColor } from "./types";

export function parseHexColor(hex: string): RgbaColor | null {
  if (!hex || typeof hex !== "string") {
    return null;
  }

  const normalized = hex.trim().replace(/^#/, "");

  if (/^[0-9a-fA-F]{3}$/.test(normalized)) {
    const r = parseInt(normalized[0] + normalized[0], 16);
    const g = parseInt(normalized[1] + normalized[1], 16);
    const b = parseInt(normalized[2] + normalized[2], 16);
    return { r, g, b, a: 255 };
  }

  if (/^[0-9a-fA-F]{6}$/.test(normalized)) {
    return {
      r: parseInt(normalized.slice(0, 2), 16),
      g: parseInt(normalized.slice(2, 4), 16),
      b: parseInt(normalized.slice(4, 6), 16),
      a: 255,
    };
  }

  if (/^[0-9a-fA-F]{8}$/.test(normalized)) {
    return {
      r: parseInt(normalized.slice(0, 2), 16),
      g: parseInt(normalized.slice(2, 4), 16),
      b: parseInt(normalized.slice(4, 6), 16),
      a: parseInt(normalized.slice(6, 8), 16),
    };
  }

  return null;
}

const RGB_RE =
  /^rgba?\(\s*([0-9.]+)\s*,\s*([0-9.]+)\s*,\s*([0-9.]+)(?:\s*,\s*([0-9.]+))?\s*\)$/i;

export function parseRgbColor(color: string): RgbaColor | null {
  const match = color.trim().match(RGB_RE);
  if (!match) {
    return null;
  }

  return {
    r: Math.max(0, Math.min(255, Math.round(Number(match[1])))),
    g: Math.max(0, Math.min(255, Math.round(Number(match[2])))),
    b: Math.max(0, Math.min(255, Math.round(Number(match[3])))),
    a:
      match[4] !== undefined
        ? Math.max(0, Math.min(255, Math.round(Number(match[4]) * 255)))
        : 255,
  };
}

export function resolveCssColor(color: string): RgbaColor | null {
  if (!color || typeof color !== "string") {
    return null;
  }

  const trimmed = color.trim();
  const hex = parseHexColor(trimmed);
  if (hex) {
    return hex;
  }

  const rgb = parseRgbColor(trimmed);
  if (rgb) {
    return rgb;
  }

  if (typeof document === "undefined") {
    return null;
  }

  try {
    const canvas = document.createElement("canvas");
    canvas.width = 1;
    canvas.height = 1;
    const ctx = canvas.getContext("2d");
    if (!ctx) {
      return null;
    }

    ctx.clearRect(0, 0, 1, 1);
    ctx.fillStyle = "#000000";
    ctx.fillStyle = trimmed;
    ctx.fillRect(0, 0, 1, 1);
    const data = ctx.getImageData(0, 0, 1, 1).data;
    if (data[3] === 0) {
      return null;
    }
    return { r: data[0], g: data[1], b: data[2], a: data[3] };
  } catch (error) {
    console.warn(
      "[rainfall-interpolation] Could not resolve legend color.",
      color,
      error
    );
    return null;
  }
}

export function applyOpacity(color: RgbaColor, opacity: number): RgbaColor {
  const clamped = Math.max(0, Math.min(1, opacity));
  return {
    ...color,
    a: Math.round((color.a / 255) * 255 * clamped),
  };
}

export function sampleRainfallBandColor(
  valueMm: number,
  bands: RainfallBand[],
  selectedBandCodes: number[],
  opacity: number,
  colorByCodigo?: Map<number, RgbaColor>
): RgbaColor | null {
  if (!Number.isFinite(valueMm) || !bands.length || !selectedBandCodes.length) {
    return null;
  }

  const band = findBandForValue(valueMm, bands);
  if (!band) {
    return null;
  }

  const selected = new Set(selectedBandCodes.map(Number));
  if (!selected.has(Number(band.codigo))) {
    return null;
  }

  if (colorByCodigo?.has(Number(band.codigo))) {
    return colorByCodigo.get(Number(band.codigo)) ?? null;
  }

  const rgba = resolveCssColor(band.color);
  if (!rgba) {
    return null;
  }
  return applyOpacity(rgba, opacity);
}

export function buildBandColorLookup(
  bands: RainfallBand[],
  selectedBandCodes: number[],
  opacity: number
): Map<number, RgbaColor> {
  const selected = new Set(selectedBandCodes.map(Number));
  const lookup = new Map<number, RgbaColor>();

  bands.forEach((band) => {
    if (!selected.has(Number(band.codigo))) {
      return;
    }
    const rgba = resolveCssColor(band.color);
    if (!rgba) {
      return;
    }
    lookup.set(Number(band.codigo), applyOpacity(rgba, opacity));
  });

  return lookup;
}
